import { Category } from '../../../types'
import { FiltersState } from './_state'
import { CounterField } from 'application/ui/fields/counter-field'

/**
 * Класс для управления DOM элементами фильтров
 */
export class FiltersDOM {
  private element: HTMLElement
  private state: FiltersState
  private categories: Category[]
  private playersCountField: CounterField | null = null

  // DOM элементы
  private typeDropdown: HTMLElement | null = null
  private categoriesDropdown: HTMLElement | null = null
  private horrorDropdown: HTMLElement | null = null
  private complexityDropdown: HTMLElement | null = null
  private metroDropdown: HTMLElement | null = null
  private ageDropdown: HTMLElement | null = null
  private moreFiltersButton: HTMLElement | null = null
  private resetButton: HTMLElement | null = null
  private playersButton: HTMLElement | null = null
  private playersCountEl: HTMLElement | null = null

  /**
   * Создаёт экземпляр FiltersDOM
   *
   * @param {HTMLElement} element - Корневой элемент
   * @param {FiltersState} state - Состояние фильтров
   * @param {Category[]} categories - Массив категорий
   */
  constructor(
    element: HTMLElement,
    state: FiltersState,
    categories: Category[]
  ) {
    this.element = element
    this.state = state
    this.categories = categories
    this.getElements()
  }

  private getElements(): void {
    this.typeDropdown = this.element.querySelector(
      '[data-catalog-filter="type"] [data-dropdown]'
    )
    this.categoriesDropdown = this.element.querySelector(
      '[data-catalog-filter="categories"] [data-dropdown]'
    )
    this.horrorDropdown = this.element.querySelector(
      '[data-catalog-filter="horror"] [data-dropdown]'
    )
    this.complexityDropdown = this.element.querySelector(
      '[data-catalog-filter="complexity"] [data-dropdown]'
    )
    this.metroDropdown = this.element.querySelector(
      '[data-catalog-filter="metro"] [data-dropdown]'
    )
    this.ageDropdown = this.element.querySelector(
      '[data-catalog-filter="age"] [data-dropdown]'
    )
    this.playersButton = this.element.querySelector(
      '[data-catalog-filter="players"]'
    )
    this.moreFiltersButton = this.element.querySelector(
      '[data-catalog-filter="more"]'
    )
    this.resetButton = this.element.querySelector(
      '[data-catalog-filter="reset"]'
    )

    this.playersCountEl = this.playersButton?.nextElementSibling as HTMLElement

    if (this.playersCountEl) {
      this.playersCountField = new CounterField(this.playersCountEl)
    }
  }

  public getPlayersButton(): HTMLElement | null {
    return this.playersButton
  }

  public getMoreFiltersButton(): HTMLElement | null {
    return this.moreFiltersButton
  }

  public getResetButton(): HTMLElement | null {
    return this.resetButton
  }

  public getElement(): HTMLElement {
    return this.element
  }

  public getPlayersCountField(): CounterField | null {
    return this.playersCountField
  }

  public getPlayersCountEl(): HTMLElement | null {
    return this.playersCountEl
  }

  public updatePlayersButton(): void {
    if (!this.playersButton) return

    if (this.state.getWithPlayers()) {
      this.playersButton.classList.add('catalog-filters__button_active')
    } else {
      this.playersButton.classList.remove('catalog-filters__button_active')
    }
  }

  public updateResetButton(): void {
    if (!this.resetButton) return

    const isActive = this.state.isActiveFilter()

    this.resetButton.classList.toggle('is-active', isActive)
  }

  public updateMoreFiltersButton(): void {
    if (!this.moreFiltersButton) return

    const hasNonDefault = this.state.hasNonDefaultAdditionalFilters()
    const showFilters = this.state.getShowFilters()

    if (hasNonDefault || showFilters) {
      this.moreFiltersButton.classList.add('catalog-filters__button_active')
    } else {
      this.moreFiltersButton.classList.remove('catalog-filters__button_active')
    }
  }

  public updateActiveCategories(): void {
    if (!this.categoriesDropdown) return

    const activeCategories = this.getActiveCategories()

    // Отправляем событие для обновления опций дропдауна категорий
    const event = new CustomEvent('update-options', {
      detail: { options: activeCategories }
    })
    this.categoriesDropdown.dispatchEvent(event)
  }

  private getActiveCategories(): Category[] {
    return this.categories
  }

  public syncDropdowns(): void {
    // Синхронизируем значения с дропдаунами через кастомные события
    if (this.typeDropdown) {
      this.typeDropdown.dispatchEvent(
        new CustomEvent('set-value', {
          detail: { value: this.state.getType() }
        })
      )
    }

    if (this.categoriesDropdown) {
      this.categoriesDropdown.dispatchEvent(
        new CustomEvent('set-value', {
          detail: { value: this.state.getCategories() }
        })
      )
    }

    if (this.horrorDropdown) {
      this.horrorDropdown.dispatchEvent(
        new CustomEvent('set-value', {
          detail: { value: this.state.getHorror() }
        })
      )
    }

    if (this.complexityDropdown) {
      this.complexityDropdown.dispatchEvent(
        new CustomEvent('set-value', {
          detail: { value: this.state.getComplexity() }
        })
      )
    }

    if (this.metroDropdown) {
      this.metroDropdown.dispatchEvent(
        new CustomEvent('set-value', {
          detail: { value: this.state.getMetro() }
        })
      )
    }

    if (this.ageDropdown) {
      this.ageDropdown.dispatchEvent(
        new CustomEvent('set-value', {
          detail: { value: this.state.getAge() }
        })
      )
    }
  }

  /**
   * Приводит состояние в соответствие с разметкой: город может скрыть часть
   * фильтров (настройки в админке), и тогда их контролов на странице нет.
   * Значение такого фильтра могло приехать из URL — применять его нельзя,
   * иначе выдача окажется отфильтрованной без единого видимого контрола.
   */
  public dropHiddenFilters(): void {
    if (!this.element.querySelector('[data-catalog-filter="type"]')) {
      this.state.setType('')
    }
    if (!this.element.querySelector('[data-catalog-filter="categories"]')) {
      this.state.setCategories([])
    }
    if (!this.element.querySelector('[data-catalog-filter="metro"]')) {
      this.state.setMetro([])
    }
    if (!this.element.querySelector('[data-catalog-filter="horror"]')) {
      this.state.setHorror([])
    }
    if (!this.element.querySelector('[data-catalog-filter="complexity"]')) {
      this.state.setComplexity([])
    }
    if (!this.element.querySelector('[data-catalog-filter="age"]')) {
      this.state.setAge('')
    }
    if (!this.element.querySelector('[data-catalog-filter="players"]')) {
      this.state.setWithPlayers(false)
    }
  }

  public resetDropdowns(): void {
    // Сбрасываем дропдауны через событие dropdown-reset
    if (this.typeDropdown) {
      this.typeDropdown.dispatchEvent(new CustomEvent('dropdown-reset'))
    }
    if (this.categoriesDropdown) {
      this.categoriesDropdown.dispatchEvent(new CustomEvent('dropdown-reset'))
    }
    if (this.horrorDropdown) {
      this.horrorDropdown.dispatchEvent(new CustomEvent('dropdown-reset'))
    }
    if (this.complexityDropdown) {
      this.complexityDropdown.dispatchEvent(new CustomEvent('dropdown-reset'))
    }
    if (this.metroDropdown) {
      this.metroDropdown.dispatchEvent(new CustomEvent('dropdown-reset'))
    }
    if (this.ageDropdown) {
      this.ageDropdown.dispatchEvent(new CustomEvent('dropdown-reset'))
    }
  }

  public dispatchEvent(event: CustomEvent): void {
    this.element.dispatchEvent(event)
  }
}
